import { Controller } from "@hotwired/stimulus"

// Shared state across all controller instances
let sharedDraggedRow = null
let sharedSourceTable = null
let sharedLastInsertPosition = null

// Connects to data-controller="form-track-sortable"
// Handles drag-and-drop sorting of track rows in project edit form
// Updates hidden fields instead of sending to server
export default class extends Controller {
  static values = {
    folderId: String  // The folder ID from the subform div id
  }

  connect() {
    this.boundDragStart = this.dragStart.bind(this)
    this.boundDragOver = this.dragOver.bind(this)
    this.boundDragEnter = this.dragEnter.bind(this)
    this.boundDrop = this.drop.bind(this)
    this.boundDragEnd = this.dragEnd.bind(this)

    // Make all track rows draggable (but not the drop-here or new-warning rows)
    this.trackRows.forEach(row => {
      row.draggable = true
      row.addEventListener('dragstart', this.boundDragStart)
      row.addEventListener('dragend', this.boundDragEnd)
    })

    // Add dragover and drop listeners to the table
    this.element.addEventListener('dragover', this.boundDragOver)
    this.element.addEventListener('dragenter', this.boundDragEnter)
    this.element.addEventListener('drop', this.boundDrop)

    // Also add listeners to all rows for better drop zone coverage
    Array.from(this.element.querySelectorAll('tr')).forEach(row => {
      row.addEventListener('dragover', this.boundDragOver)
      row.addEventListener('drop', this.boundDrop)
    })
  }

  disconnect() {
    this.trackRows.forEach(row => {
      row.draggable = false
      row.removeEventListener('dragstart', this.boundDragStart)
      row.removeEventListener('dragend', this.boundDragEnd)
    })

    this.element.removeEventListener('dragover', this.boundDragOver)
    this.element.removeEventListener('dragenter', this.boundDragEnter)
    this.element.removeEventListener('drop', this.boundDrop)

    Array.from(this.element.querySelectorAll('tr')).forEach(row => {
      row.removeEventListener('dragover', this.boundDragOver)
      row.removeEventListener('drop', this.boundDrop)
    })
  }

  get trackRows() {
    return this.element.querySelectorAll('tr.project_folders_recording_mixes')
  }

  dragStart(event) {
    // Only handle if dragging a track row
    if (!event.target.classList.contains('project_folders_recording_mixes')) return

    // Stop propagation to prevent folder-level drag from activating
    event.stopPropagation()

    sharedDraggedRow = event.target
    sharedSourceTable = sharedDraggedRow.closest('table')
    sharedDraggedRow.style.opacity = '0.5'
    sharedLastInsertPosition = null

    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/html', sharedDraggedRow.innerHTML)
  }

  dragEnter(event) {
    if (!sharedDraggedRow) return
    event.preventDefault()
  }

  dragOver(event) {
    if (!sharedDraggedRow) return

    // Check if we're over THIS controller's table element
    const targetTable = event.target.closest('table.children_fields')

    // Only handle if this event is for this table
    if (!targetTable || targetTable !== this.element) return

    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'

    // Check if we're over another track row (not the one being dragged)
    const targetRow = event.target.closest('tr.project_folders_recording_mixes')

    if (targetRow && targetRow !== sharedDraggedRow && targetRow.parentNode === targetTable.querySelector('tbody')) {
      // Dragging over another track row - insert before or after
      const rect = targetRow.getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2
      let insertBeforeElement = null

      const tbody = targetTable.querySelector('tbody')

      if (event.clientY < midpoint) {
        // Insert before target
        insertBeforeElement = targetRow
      } else {
        // Insert after target
        insertBeforeElement = targetRow.nextElementSibling
      }

      // Only insert if position has changed
      if (insertBeforeElement !== sharedLastInsertPosition) {
        sharedLastInsertPosition = insertBeforeElement
        tbody.insertBefore(sharedDraggedRow, insertBeforeElement)
      }
    } else {
      // Dragging over placeholder row, empty space, or a different table
      const tbody = targetTable.querySelector('tbody')
      const currentDraggedTable = sharedDraggedRow.closest('table')

      if (currentDraggedTable !== targetTable) {
        // Moving to a different table - insert before placeholder or at beginning
        const placeholder = tbody.querySelector('tr.drop-here')
        if (placeholder) {
          tbody.insertBefore(sharedDraggedRow, placeholder)
        } else {
          // No placeholder - check if there are any track rows
          const firstTrack = tbody.querySelector('tr.project_folders_recording_mixes')
          if (firstTrack) {
            tbody.insertBefore(sharedDraggedRow, firstTrack)
          } else {
            // Completely empty, just append (but before drop-here if it exists)
            const dropHere = tbody.querySelector('tr.drop-here')
            tbody.insertBefore(sharedDraggedRow, dropHere)
          }
        }
      } else {
        // Same table, not over a valid track row - don't do anything
        // The placeholder is always present, so we shouldn't use it for logic here
      }
    }
  }

  drop(event) {
    if (!sharedDraggedRow) return

    // Only handle drop if it's on this table
    const targetTable = event.target.closest('table.children_fields')
    if (!targetTable || targetTable !== this.element) return

    event.preventDefault()
    return false
  }

  dragEnd(event) {
    if (!sharedDraggedRow) return

    // Stop propagation to prevent folder-level drag from interfering
    event.stopPropagation()

    sharedDraggedRow.style.opacity = ''

    const currentTable = sharedDraggedRow.closest('table')

    // Update empty class on both source and destination tables
    this.updateEmptyState(sharedSourceTable)
    if (currentTable !== sharedSourceTable) {
      this.updateEmptyState(currentTable)

      // Update the folder ID for the moved track
      this.updateFolderId(sharedDraggedRow, currentTable)

      // Update positions in the target table
      const targetController = this.application.getControllerForElementAndIdentifier(
        currentTable,
        'form-track-sortable'
      )
      if (targetController) {
        targetController.updatePositions()
      }
    }

    // Update positions in the source (or current if same) table
    this.updatePositions()

    sharedDraggedRow = null
    sharedSourceTable = null
    sharedLastInsertPosition = null
  }

  updateEmptyState(table) {
    if (!table) return

    const hasTrackRows = table.querySelectorAll('tr.project_folders_recording_mixes').length > 0
    if (hasTrackRows) {
      table.classList.remove('empty')
    } else {
      table.classList.add('empty')
    }
  }

  updateFolderId(row, targetTable) {
    // Find the folder ID from the target table's parent div
    const subformDiv = targetTable.closest('div.subform')
    if (!subformDiv) return

    // Extract folder ID from the div id: "project_folders_recording_mixes_123"
    const match = subformDiv.id.match(/project_folders_recording_mixes_(.+)$/)
    if (!match) return

    const folderId = match[1]

    // Find and update the protected_project_folder_id hidden field
    const folderIdField = row.querySelector('input[name$="[protected_project_folder_id]"]')
    if (folderIdField) {
      folderIdField.value = folderId
    }
  }

  updatePositions() {
    // Update position fields for all tracks in this table
    const trackRows = this.element.querySelectorAll('tr.project_folders_recording_mixes')
    trackRows.forEach((row, index) => {
      const positionField = row.querySelector('input[name$="[position]"]')
      if (positionField) {
        positionField.value = index + 1
      }
    })
  }
}
